/**
 * staticNav.ts — static fallback menu entries for routes that are reachable
 * by URL but are not yet included in the backend-supplied JWT MenuDTO payload.
 *
 * Only injected into the nav tree for ADMIN users (gate is in AppShell.tsx).
 * Negative IDs guarantee zero collision with any backend-supplied menu IDs.
 * Permission arrays are set to all-CRUD [1,2,3,4] so hasPermission always
 * passes; the visibility gate is the ADMIN check in AppShell, not the
 * permission filter inside AppNavList.
 */
import type { MenuItem } from '@/core/rbac/types';

const ALL_PERMS = [1, 2, 3, 4];

export const STATIC_FALLBACK_MENU: MenuItem[] = [
  {
    id: -1,
    name: 'Configuration',
    unique: false,
    route: 'configuration',
    icon: 'settings',
    iconType: 'material-icons',
    selected: false,
    permission: ALL_PERMS,
    labelTranslate: 'nav.fallback.configuration',
    subMenus: [
      {
        id: -101,
        name: 'Roles',
        route: 'roles',
        icon: 'admin_panel_settings',
        iconType: 'material-icons',
        selected: false,
        permission: ALL_PERMS,
        unique: true,
        isMenu: true,
        labelTranslate: 'nav.fallback.roles',
      },
      {
        id: -102,
        name: 'Users',
        route: 'users',
        icon: 'people',
        iconType: 'material-icons',
        selected: false,
        permission: ALL_PERMS,
        unique: true,
        isMenu: true,
        labelTranslate: 'nav.fallback.users',
      },
      /**
       * Applications.
       *
       * Same reason as Tow Truck Drivers below: the route and the endpoint
       * behind it exist and ADMIN is already permitted, and only the
       * SysSubMenu row is missing — which would mean running a seeder against
       * the production database. Shipped with the frontend instead.
       *
       * First in the group on purpose. It is the only entry here where
       * somebody is waiting on the other end.
       */
      {
        id: -104,
        name: 'Applications',
        route: 'applications',
        icon: 'how_to_reg',
        iconType: 'material-icons',
        selected: false,
        permission: ALL_PERMS,
        unique: true,
        isMenu: true,
        labelTranslate: 'nav.fallback.applications',
      },
      /**
       * Dispatch Settings.
       *
       * Same reason as Applications and Activity: the route and the endpoint
       * behind it exist and the API refuses anybody but ADMIN, and only the
       * SysSubMenu row is missing. Shipped with the frontend rather than by
       * seeding the production database.
       *
       * Under Configuration on purpose. It is a settings screen, so it belongs
       * in the Setup section of the rail — and filing it here gets that for
       * free, because `sectionForRoute` sections on the parent's route.
       */
      {
        id: -106,
        name: 'Dispatch Settings',
        route: 'dispatch-settings',
        icon: 'tune',
        iconType: 'material-icons',
        selected: false,
        permission: ALL_PERMS,
        unique: true,
        isMenu: true,
        labelTranslate: 'nav.fallback.dispatchSettings',
      },
      /**
       * Telematics Devices. Same reason as Applications: route and endpoint
       * exist (ADMIN only), no SysSubMenu row, so the entry ships here.
       */
      {
        id: -107,
        name: 'Telematics Devices',
        route: 'telematics',
        icon: 'sensors',
        iconType: 'material-icons',
        selected: false,
        permission: ALL_PERMS,
        unique: true,
        isMenu: true,
        labelTranslate: 'nav.fallback.telematics',
      },
      {
        id: -105,
        name: 'Activity',
        route: 'activity',
        icon: 'timeline',
        iconType: 'material-icons',
        selected: false,
        permission: ALL_PERMS,
        unique: true,
        isMenu: true,
        labelTranslate: 'nav.fallback.activity',
      },
      {
        id: -103,
        name: 'Terms',
        route: 'terms-conditions',
        icon: 'gavel',
        iconType: 'material-icons',
        selected: false,
        permission: ALL_PERMS,
        unique: true,
        isMenu: true,
        labelTranslate: 'nav.fallback.termsConditions',
      },
    ],
  },
  {
    id: -2,
    name: 'Routings',
    unique: true,
    route: 'routings',
    icon: 'alt_route',
    iconType: 'material-icons',
    selected: false,
    permission: ALL_PERMS,
    labelTranslate: 'nav.fallback.routings',
    subMenus: [],
  },
  /**
   * Tow Truck Drivers.
   *
   * The route exists and the API behind it is already permitted — the panel
   * calls DriverController, which gates on `catalogs/drivers`, and ADMIN holds
   * that. Only the SysSubMenu row is missing, and seeding one would mean
   * running the dev seeder against the production database. This ships the nav
   * entry with the frontend instead.
   */
  {
    id: -4,
    name: 'Tow Truck Drivers',
    unique: true,
    route: 'catalogs/tow-truck-drivers',
    icon: 'local_shipping',
    iconType: 'material-icons',
    selected: false,
    permission: ALL_PERMS,
    labelTranslate: 'nav.fallback.towTruckDrivers',
    subMenus: [],
  },
  {
    id: -3,
    name: 'Documents',
    unique: true,
    route: 'catalogs/documents',
    icon: 'description',
    iconType: 'material-icons',
    selected: false,
    permission: ALL_PERMS,
    labelTranslate: 'nav.fallback.documents',
    subMenus: [],
  },
];

/**
 * Folds the static entries into the menu the server sent.
 *
 * This used to drop any static group whose *name* the backend menu already
 * had. That reads as sensible and is the bug: the backend menu has always had
 * a Configuration group, so the static Configuration group -- and every entry
 * inside it -- was discarded in one go. Nobody noticed for as long as its
 * children were all things the backend also provided; the first genuinely new
 * child added to it simply never appeared, with no error anywhere.
 *
 * So collisions merge rather than discard. A static group whose name is
 * already present contributes its children to the existing one, and only the
 * children that are themselves already there are skipped.
 *
 * Children are matched on the **last segment** of the route, not the whole
 * string. The server stores full paths -- `configuration/roles` -- while these
 * entries are relative to their parent -- `roles` -- so comparing the strings
 * whole matches nothing and duplicates every child that already existed. Name
 * is checked as well, because a label is a weaker signal than a route but
 * catches a row whose path has been spelled differently again.
 */
/** `configuration/roles` and `roles` are the same entry. Compare the leaf. */
function lastSegment(route: string | undefined | null): string {
  if (route === undefined || route === null) return '';
  const parts = route.split('/').filter(Boolean);
  return (parts[parts.length - 1] ?? '').toLowerCase();
}

export function mergeStaticNav(serverMenu: MenuItem[], staticMenu: MenuItem[]): MenuItem[] {
  const byName = new Map(serverMenu.map((m) => [m.name.toLowerCase(), m]));
  const merged = serverMenu.map((m) => ({ ...m }));
  const appended: MenuItem[] = [];

  for (const group of staticMenu) {
    const existing = byName.get(group.name.toLowerCase());

    if (existing === undefined) {
      appended.push(group);
      continue;
    }

    const target = merged.find((m) => m.name.toLowerCase() === group.name.toLowerCase());
    if (target === undefined) continue;

    const known = new Set<string>();
    for (const child of target.subMenus ?? []) {
      const leaf = lastSegment(child.route);
      if (leaf !== '') known.add(leaf);
      if (child.name) known.add(child.name.toLowerCase());
    }

    const additions = (group.subMenus ?? []).filter(
      (c) =>
        !known.has(lastSegment(c.route)) && !known.has((c.name ?? '').toLowerCase()),
    );

    if (additions.length > 0) {
      target.subMenus = [...additions, ...(target.subMenus ?? [])];
    }
  }

  return [...merged, ...appended];
}
